<template>
  <fieldset
    v-if="item.id"
    class="cart-drawer-item"
  >
    <component
      :is="isGiftItem ? 'div' : 'a'"
      :aria-label="item.product_title"
      :href="isGiftItem ? null : item.url"
      class="cart-drawer-item__image-wrapper"
    >
      <responsive-image
        class="cart-drawer-item__image"
        :image="getItemImage"
        min-max="100-600"
        sizes="150px, (min-width: 768px) 200px"
      />
    </component>

    <div class="cart-drawer-item__details">
      <h3 class="cart-drawer-item__title">
        <component
          :is="isGiftItem ? 'span' : 'a'"
          :href="isGiftItem ? null : item.url"
          v-text="item.product_title"
        />
      </h3>

      <p
        v-if="getItemSubtitle"
        class="cart-drawer-item__subtitle"
        v-html="getItemSubtitle"
      />

      <product-prices
        class="cart-drawer-item__prices"
        :product="formatItemPrices"
      />
    </div>

    <div class="cart-drawer-item__controls">
      <div
        v-if="!isGiftItem"
        class="cart-drawer-item__quantity"
      >
        <button
          class="cart-drawer-item__quantity-action button"
          type="button"
          :aria-label="decreaseQuantityLabel"
          @click="handleChangeQuantity(-1)"
          v-text="'−'"
        />

        <span
          class="cart-drawer-item__quantity-value"
          v-text="item.quantity"
        />

        <button
          class="cart-drawer-item__quantity-action button"
          type="button"
          :aria-label="increaseQuantityLabel"
          :disabled="disableIncreaseQuantity"
          @click="handleChangeQuantity(1)"
          v-text="'+'"
        />
      </div>

      <button
        class="cart-drawer-item__remove button button--underline"
        type="button"
        @click="handleRemoveItem()"
        v-text="removeLabel"
      />
    </div>
  </fieldset>
</template>

<script>

/**
 * Component: Cart drawer item
 * ------------------------------------------------------------------------------
 * - Represents a line item within the cart drawer.
 *
 * @param {Object} item - Cart drawer line item.
 */
import { mapActions, mapState } from 'pinia'

import { useCartStore } from '~stores/cart'

import { getFormattedImages } from '~/format/product'
import { getInternationalSizing } from '~/format/utils'
import { getPackShotImage } from '~/helpers/utils'
import { stringToHandle } from '~/helpers/strings'

import ProductPrices from '~components/product-prices/product-prices'
import ResponsiveImage from '~components/responsive-image/responsive-image'

export default {
  name: 'CartDrawerItem',

  components: {
    ProductPrices,
    ResponsiveImage,
  },

  props: {
    item: {
      type: Object,
      default: () => {},
    },
  },

  /**
   * This event is used to prevent modifications to the cart while a request
   * is being processed.
   */
  emits: ['lock-drawer'],

  data() {
    return {
      decreaseQuantityLabel: mint.Strings('cart.general.decrease'),
      increaseQuantityLabel: mint.Strings('cart.general.increase'),
      perItemLimit: 20,
      removeLabel: mint.Strings('cart.general.remove'),
    }
  },

  computed: {

    /**
     * Map the cart data.
     */
    ...mapState(useCartStore, ['cartData']),

    /**
     * Return true if the "increase quantity" button should be disabled.
     * - Early return for gift cards, as these should be added manually
     * - Prevent increasing quantity beyond the variant's inventory
     * @returns {Boolean}
     */
    disableIncreaseQuantity() {
      const { inventory_quantity, product_type, quantity } = this.item

      if (product_type === 'Gift Cards') {
        return true
      }

      return quantity >= inventory_quantity || quantity >= this.perItemLimit
    },

    /**
     * Determines whether the item is a `Gift Wrap` or `Gift Receipt`
     * @returns {Boolean}
     */
    isGiftItem() {
      const { handle } = this.item
      let isGift = false

      switch (handle) {
        case 'gift-wrap':
          isGift = true
          break
        case 'gift-receipt':
          isGift = true
          break

        default:
          isGift = false
          break
      }

      return isGift
    },

    /**
     * Format the item's prices as an object for use with the ProductPrices
     * component.
     * @returns {Object}
     */
    formatItemPrices() {
      const {
        compare_at_price: compareAtAmount,
        handle,
        price: priceAmount,
      } = this.item

      return {
        compareAtPrice: { amount: compareAtAmount },
        handle,
        price: { amount: priceAmount },
      }
    },

    /**
     * Select an image for the item.
     * @returns {Object}
     */
    getItemImage() {
      const { cloudinary_images, images, product_title: title } = this.item

      const formattedImages = getFormattedImages({
        title,
        images,
        metaCloudinaryMediaInformation: { value: cloudinary_images },
      })

      const packShot = getPackShotImage(formattedImages)

      return packShot.image || false
    },

    /**
     * Generate subtitle text for the line item.
     * @returns {String|Boolean}
     */
    getItemSubtitle() {
      if (this.isGiftItem) {
        return false
      }

      const {
        international_sizes,
        options_with_values,
        product_type,
        variant_title,
      } = this.item

      /**
       * Gift cards have several options, so output as a single line
       */
      if (product_type === 'Gift Cards') {
        return variant_title
      }

      /**
       * For all other products, display options and values, and handle
       * international sizes.
       */
      const options = options_with_values || []

      const formattedOptions = options.map(option => {
        const { name, value } = option

        if (stringToHandle(name) === 'size') {
          const internationalSize = getInternationalSizing(
            value,
            international_sizes,
          )

          /**
           * Fall back to the original value if a matching international size
           * does not exist.
           */
          option.value = internationalSize || value
        }

        return `${name} ${option.value}`
      })

      return formattedOptions.join('<br>')
    },
  },

  methods: {

    /**
     * Map cart actions.
     */
    ...mapActions(useCartStore, ['changeItem', 'removeItem']),

    /**
     * Handle changes to an item's quantity.
     * @param {Number} change
     */
    async handleChangeQuantity(change = 1) {
      const {
        inventory_quantity,
        key: id,
        quantity,
        variant_id,
      } = this.item

      const targetQuantity = quantity + change

      /**
       * Early return if the target quantity is zero (or below, somehow)
       */
      if (targetQuantity < 1) {
        this.handleRemoveItem()
        return
      }

      /**
       * Early return if the target quantity exceeds inventory
       */
      if (targetQuantity > inventory_quantity) {
        return
      }

      const payload = {
        data: { id, quantity: targetQuantity },
        meta: {
          eventType: 'change_quantity',
          id: variant_id,
        },
      }

      this.$emit('lock-drawer', true)

      try {
        await this.changeItem(payload)
      } catch (error) {
        this.$emit('lock-drawer', false)

        mint.Log(
          'error',
          `Failed to update quantity for cart item: ${this.item}`,
          error,
        )
      }
    },

    /**
     * Handle removing the item from the cart.
     */
    async handleRemoveItem() {
      const { key } = this.item

      this.$emit('lock-drawer', true)

      try {
        await this.removeItem({ key })
      } catch (error) {
        this.$emit('lock-drawer', false)

        mint.Log(
          'error',
          `Failed to remove cart item: ${this.item}`,
          error,
        )
      }
    },
  },
}
</script>

<style src="./cart-drawer-item.scss" lang="scss"></style>
